import styled from '@emotion/styled';

type TextTone = 'light' | 'dark';

interface HeaderStateProps {
	isMegaOpen?: boolean;
	isAuthPath?: boolean;
	isScrolled?: boolean;
	$solid?: boolean;
}
interface ToneProps {
	$tone?: TextTone;
}
interface MainMenuItemProps extends ToneProps {
	$isActive?: boolean;
}

const navColor = (tone: TextTone | undefined, theme: { ui: { text: { inverse: string; primary: string } } }) =>
	tone === 'light' ? theme.ui.text.inverse : theme.ui.text.primary;

export const HeaderContainer = styled.header<HeaderStateProps>`
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	background: ${(props) => (props.$solid ? props.theme.ui.surface.headerSolid : props.theme.ui.surface.headerOverlay)};
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	height: ${(props) => (props.isMegaOpen ? props.theme.heights.headerMega : props.theme.heights.header)};
	z-index: 10;
	border-bottom: ${(props) =>
		props.isAuthPath || props.isScrolled || props.isMegaOpen
			? `1px solid ${props.theme.ui.border.default}`
			: 'none'};
	box-shadow: ${(props) =>
		props.isAuthPath || props.isScrolled || props.isMegaOpen ? props.theme.effects.shadowHeader : 'none'};
	transition:
		height ${(props) => props.theme.effects.transitionBase},
		background ${(props) => props.theme.effects.transitionBase},
		box-shadow ${(props) => props.theme.effects.transitionBase},
		border-color ${(props) => props.theme.effects.transitionBase};
	@media screen and (max-width: 1180px) {
		height: ${(props) => props.theme.heights.headerMobile};
	}
`;
export const Nav = styled.nav`
	position: relative;
	display: flex;
	flex-direction: column;
	max-width: ${(props) => props.theme.scale.contentMaxWidth};
	width: 100%;
	height: 100%;
	min-height: 0;
	margin: 0 auto;
	padding: 0 ${(props) => props.theme.scale.contentPaddingX};
	box-sizing: border-box;
`;
export const TopBar = styled.div`
	display: grid;
	grid-template-columns: 11rem minmax(0, 1fr) auto;
	align-items: center;
	column-gap: ${(props) => props.theme.scale.spaceLg};
	flex-shrink: 0;
	height: ${(props) => props.theme.heights.header};
	min-height: 0;
	overflow: visible;

	@media screen and (max-width: 1180px) {
		grid-template-columns: auto 1fr auto;
		column-gap: ${(props) => props.theme.scale.spaceSm};
		height: ${(props) => props.theme.heights.headerMobile};
	}
`;
export const LogoBox = styled.div<{ $lifted?: boolean }>`
	display: flex;
	align-items: center;
	transform: ${(props) => (props.$lifted ? 'translateY(-0.5rem)' : 'translateY(0)')};
	transition: transform ${(props) => props.theme.effects.transitionBase};
`;
export const ImgBox = styled.div<{ $tone?: TextTone }>`
	width: 10.4rem;
	img {
		display: block;
		width: 100%;
		filter: ${(props) => (props.$tone === 'light' ? 'brightness(0) invert(1)' : 'none')};
		transition: filter ${(props) => props.theme.effects.transitionFast};
	}
`;
export const MainMenu = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: nowrap;
	gap: ${(props) => props.theme.scale.space2xs};
	min-width: 0;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}

	@media screen and (max-width: 1320px) {
		justify-content: flex-start;
	}

	@media screen and (max-width: 1180px) {
		display: none;
	}
`;
export const MainMenuItem = styled.button<MainMenuItemProps>`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: ${(props) => props.theme.scale.space3xs};
	height: ${(props) => props.theme.heights.header};
	padding: 0 ${(props) => props.theme.scale.spaceSm};
	border: none;
	background: transparent;
	color: ${(props) => navColor(props.$tone, props.theme)};
	font-size: ${(props) => props.theme.ui.nav.mainFontSize};
	font-weight: ${(props) => props.theme.ui.nav.mainFontWeight};
	line-height: 1.2;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	white-space: nowrap;
	flex-shrink: 0;
	cursor: pointer;
	transition: color ${(props) => props.theme.effects.transitionFast};

	&::after {
		content: '';
		position: absolute;
		left: ${(props) => props.theme.scale.spaceSm};
		right: ${(props) => props.theme.scale.spaceSm};
		bottom: 1.4rem;
		height: ${(props) => props.theme.ui.nav.underlineHeight};
		background: ${(props) =>
			props.$tone === 'light' ? props.theme.ui.text.inverse : props.theme.ui.text.primary};
		transform: scaleX(${(props) => (props.$isActive ? 1 : 0)});
		transform-origin: center;
		transition: transform ${(props) => props.theme.effects.transitionBase};
	}

	&:hover {
		color: ${(props) => props.theme.ui.text.accent};
	}

	&:hover::after,
	&[data-active='true']::after {
		transform: scaleX(1);
		background: ${(props) => props.theme.ui.interactive.primary};
	}

	@media screen and (max-width: 1320px) {
		padding: 0 ${(props) => props.theme.scale.spacexs};
		font-size: ${(props) => props.theme.fontSize.sizeMd};
	}
`;
export const MegaPanel = styled.div`
	display: grid;
	flex: 1;
	min-height: 0;
	grid-template-columns: minmax(18rem, 30rem) minmax(22rem, 1fr) minmax(26rem, 1.2fr);
	grid-template-areas: 'intro links visual';
	overflow: hidden;
	border-top: 1px solid ${(props) => props.theme.ui.border.default};
	animation: megaFadeIn 0.26s cubic-bezier(0.4, 0, 0.2, 1);

	@keyframes megaFadeIn {
		from {
			opacity: 0;
			transform: translateY(6px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	& > :nth-of-type(1) {
		grid-area: intro;
	}
	& > :nth-of-type(2) {
		grid-area: links;
	}
	& > :nth-of-type(3) {
		grid-area: visual;
	}

	@media screen and (max-width: 1280px) {
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		grid-template-rows: auto minmax(0, 1fr);
		grid-template-areas:
			'intro intro'
			'links visual';

		& > :nth-of-type(3) {
			min-height: 14rem;
			max-height: 22rem;
		}
	}

	@media screen and (max-width: 1024px) {
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto minmax(0, 1fr) auto;
		grid-template-areas:
			'intro'
			'links'
			'visual';

		& > :nth-of-type(2) {
			border-left: none;
			border-top: 1px solid ${(props) => props.theme.ui.border.default};
		}

		& > :nth-of-type(3) {
			border-left: none;
			border-top: 1px solid ${(props) => props.theme.ui.border.default};
			min-height: 12rem;
			max-height: 16rem;
		}
	}
`;
export const MegaIntro = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: ${(props) => props.theme.scale.spaceSm};
	min-width: 0;
	min-height: 0;
	overflow: hidden;
	padding: ${(props) => props.theme.scale.spaceXl} ${(props) => props.theme.scale.spaceLg};
	background: ${(props) => props.theme.ui.surface.megaIntro};
`;
export const MegaLabel = styled.span`
	margin: 0;
	color: ${(props) => props.theme.ui.text.muted};
	font-size: ${(props) => props.theme.ui.mega.labelFontSize};
	font-weight: ${(props) => props.theme.ui.mega.labelFontWeight};
	line-height: 1;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.label};
	text-transform: uppercase;
`;
export const MegaTitle = styled.h2`
	margin: 0;
	color: ${(props) => props.theme.ui.text.primary};
	font-size: ${(props) => props.theme.ui.mega.titleFontSize};
	font-weight: ${(props) => props.theme.ui.mega.titleFontWeight};
	line-height: 1.3;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	word-break: keep-all;
`;
export const MegaDescription = styled.p`
	margin: 0;
	max-width: 26ch;
	color: ${(props) => props.theme.ui.text.secondary};
	font-size: ${(props) => props.theme.ui.mega.descFontSize};
	font-weight: ${(props) => props.theme.ui.mega.descFontWeight};
	line-height: 1.55;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.body};
	word-break: keep-all;
`;
export const MegaLinks = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: ${(props) => props.theme.scale.spaceLg};
	min-width: 0;
	min-height: 0;
	overflow: hidden;
	padding: ${(props) => props.theme.scale.spaceXl} ${(props) => props.theme.scale.space2xl};
	border-left: 1px solid ${(props) => props.theme.ui.border.default};
	background: ${(props) => props.theme.ui.surface.megaLinks};
`;
export const MegaLinkList = styled.ul`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
	gap: ${(props) => props.theme.scale.space2xs};
	min-height: 0;
	margin: 0;
	padding: 0;
	overflow-y: auto;
	list-style: none;
`;
export const MegaLinkItem = styled.li``;
export const MegaLinkButton = styled.button<{ $isActive?: boolean }>`
	display: block;
	width: 100%;
	margin: 0;
	padding: 0.7rem 0;
	border: none;
	border-radius: ${(props) => props.theme.effects.radiusSm};
	background: transparent;
	color: ${(props) => (props.$isActive ? props.theme.ui.text.accent : props.theme.ui.text.primary)};
	font-size: ${(props) => props.theme.ui.mega.linkFontSize};
	font-weight: ${(props) =>
		props.$isActive ? props.theme.ui.mega.linkActiveFontWeight : props.theme.ui.mega.linkFontWeight};
	line-height: 1.35;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	text-align: left;
	word-break: keep-all;
	cursor: pointer;
	transition:
		color ${(props) => props.theme.effects.transitionFast},
		background-color ${(props) => props.theme.effects.transitionFast};

	&:hover {
		color: ${(props) => props.theme.ui.text.accent};
		background-color: ${(props) => props.theme.ui.interactive.primaryMuted};
	}
`;
export const MegaViewMore = styled.button`
	display: inline-flex;
	align-items: center;
	margin-top: auto;
	padding-top: ${(props) => props.theme.scale.spaceMd};
	border: none;
	border-top: 1px solid ${(props) => props.theme.ui.border.default};
	background: transparent;
	color: ${(props) => props.theme.ui.text.secondary};
	font-size: ${(props) => props.theme.ui.mega.viewMoreFontSize};
	font-weight: ${(props) => props.theme.ui.mega.viewMoreFontWeight};
	line-height: 1;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.body};
	cursor: pointer;
	transition: color ${(props) => props.theme.effects.transitionFast};

	&:hover {
		color: ${(props) => props.theme.ui.text.accent};
	}
`;
export const MegaVisual = styled.div`
	position: relative;
	min-height: 0;
	height: 100%;
	overflow: hidden;
	border-left: 1px solid ${(props) => props.theme.ui.border.default};
`;
export const MegaVisualImage = styled.img`
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center;
`;
export const MegaVisualOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: linear-gradient(
		135deg,
		rgba(18, 83, 158, 0.15) 0%,
		rgba(18, 83, 158, 0.42) 55%,
		rgba(0, 21, 52, 0.55) 100%
	);
	pointer-events: none;
`;
export const AuthBox = styled.div<{ isLogin: boolean }>`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: ${(props) => props.theme.scale.spaceMd};
	flex-shrink: 0;
	height: 100%;

	@media screen and (max-width: 1180px) {
		gap: ${(props) => props.theme.scale.space2xs};
	}
`;
export const AuthButton = styled.button<ToneProps>`
	background: transparent;
	border: none;
	color: ${(props) => navColor(props.$tone, props.theme)};
	font-size: ${(props) => props.theme.ui.nav.authFontSize};
	font-weight: ${(props) => props.theme.ui.nav.mainFontWeight};
	line-height: 1.2;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	cursor: pointer;
	white-space: nowrap;
	transition: color ${(props) => props.theme.effects.transitionFast};

	&:hover {
		color: ${(props) => props.theme.ui.text.accent};
	}

	@media screen and (max-width: 1180px) {
		font-size: ${(props) => props.theme.fontSize.sizeSm};
	}
`;
export const UserButton = styled.button`
	padding: 0 ${(props) => props.theme.scale.spaceSm};
	border-radius: ${(props) => props.theme.effects.radiusFull};
	background: ${(props) => props.theme.ui.interactive.primary};
	height: 3.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${(props) => props.theme.scale.space3xs};
	border: none;
	color: ${(props) => props.theme.ui.text.inverse};
	font-size: ${(props) => props.theme.fontSize.sizeXs};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: normal;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.body};
	cursor: pointer;
	box-shadow: ${(props) => props.theme.effects.shadowSm};
	transition:
		background-color ${(props) => props.theme.effects.transitionFast},
		box-shadow ${(props) => props.theme.effects.transitionFast},
		transform ${(props) => props.theme.effects.transitionFast};

	&:hover {
		background-color: ${(props) => props.theme.ui.interactive.primaryHover};
		box-shadow: ${(props) => props.theme.effects.shadowMd};
		transform: translateY(-1px);
	}
`;
